"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Bell, Pin } from "lucide-react";

interface Separator {
  type: "separator";
  title?: never;
  icon?: never;
}

interface ExpandableTabsProps {
  tabs: {
    key: string;
    Component: JSX.Element;
  }[];
  MainTab: JSX.Element;
  className?: string;
  activeColor?: string;
  onChange?: (index: number | null) => void;
}

const buttonVariants = {
  initial: {
    gap: 0,
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
  },
  animate: (isSelected: boolean) => ({
    gap: isSelected ? ".5rem" : 0,
    paddingLeft: isSelected ? "1rem" : ".5rem",
    paddingRight: isSelected ? "1rem" : ".5rem",
  }),
};

const transition = { delay: 0.1, bounce: 0, duration: 0.6 };

const Separator = () => (
  <div className="mx-1 h-[24px] w-[1.2px] bg-border" aria-hidden="true" />
);

export function ExpandableTabs({
  tabs,
  MainTab,
  className,
  activeColor = "text-primary",
}: ExpandableTabsProps) {
  const [isHover, setHover] = React.useState(false);
  const [isPinned, setPinned] = React.useState(false);

  return (
    <div
      className={cn(
        "flex justify-start gap-2 rounded-lg border bg-background shadow-sm p-1",
        className,
      )}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => {
        if (!isPinned) setHover(false);
      }}
    >
      <AnimatePresence mode="wait">
        {!isHover ? (
          <motion.div
            key="collapsed"
            initial={{ width: 40 }}
            animate={{ width: 40 }}
            exit={{ width: "auto" }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="flex justify-center"
          >
            {MainTab}
            {/* <motion.button
              variants={buttonVariants}
              initial={false}
              animate="animate"
              title="Dashboard"
              transition={transition}
              className={cn(
                "relative flex items-center rounded-xl px-4 py-2 text-sm font-medium transition-colors duration-300 cursor-pointer",
                cn(activeColor),
              )}
            >
              <Bell size={20} />
            </motion.button> */}
          </motion.div>
        ) : (
          <motion.div
            key="expanded"
            initial={{ width: 48 }}
            animate={{ width: "auto" }}
            exit={{ width: 48 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="flex items-center gap-x-2 overflow-hidden"
          >
            {MainTab}
            <Separator />
            {tabs.map((Tab) => {
              return <div key={Tab.key}>{Tab.Component}</div>;
            })}
            <Separator />
            <motion.button
              key="pin"
              variants={buttonVariants}
              initial={false}
              animate="animate"
              title="Pin"
              onClick={() => setPinned((prev) => !prev)}
              transition={transition}
              className={cn(
                "relative flex items-center rounded-lg px-4 py-1 text-sm font-medium transition-colors duration-300 cursor-pointer",
                "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Pin size={20} fill={isPinned ? "#222" : "white"} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
